import { $GoalsIndexPhase, GoalsIndexPhase } from '@suntex.fim/enums';

import { IndexChartFrame } from './IndexChartFrame';
import { CHART_DIMENSIONS } from './const';
import type {
  GoalsIndexChartViewProps,
  GoalsIndexData,
  LevelDetails,
  SectorConfig,
} from './types';
import { useRaphaelChart } from './useRaphaelChart';

// Default description for hover
const defaultDescription =
  'The goals index is an overall measure of performance on First in Math in four categories: Activity, Fact Fluency, Word Problem Fluency, and Procedural Fluency Hover over the quadrants for more information.';

// Define the 4 sectors with their properties (matching reference file order)
const sectors = [
  {
    name: 'Activity',
    scorePropertyName: 'activity',
    color: '#6677ff',
    weight: 25,
    getDescription: (phase: GoalsIndexPhase) => {
      const label = $GoalsIndexPhase.describe(phase).label;
      const { stickers } = detailsByLevel[phase]!;
      return `This score measures activity across all First in Math modules. The "${label}" goal is to exceed ${stickers} stickers per student. This score is given out of 25.`;
    },
  },
  {
    name: 'Fact Fluency',
    scorePropertyName: 'fact',
    color: '#8866cc',
    weight: 25,
    getDescription: (phase: GoalsIndexPhase) => {
      const label = $GoalsIndexPhase.describe(phase).label;
      return `The "${label}" Fact Fluency score reflects skill acquisition - by operation. Addition, Subtraction, Multiplication and Division are each worth six points. Fluency score can be earned in JTF, VIFs or GYMs.`;
    },
  },
  {
    name: 'Procedural Fluency',
    scorePropertyName: 'procedural',
    color: '#ff5544',
    weight: 25,
    getDescription: (phase: GoalsIndexPhase) => {
      const label = $GoalsIndexPhase.describe(phase).label;
      const { ss } = detailsByLevel[phase]!;
      return `The "${label}" Procedural Fluency score reflects performance in Skill sets ${ss} This score is given out of 25.`;
    },
  },
  {
    name: 'Word Problem Fluency',
    scorePropertyName: 'wordProblem',
    color: '#33bb88',
    weight: 25,
    getDescription: (phase: GoalsIndexPhase) => {
      const label = $GoalsIndexPhase.describe(phase).label;
      const { ks } = detailsByLevel[phase]!;
      return `The "${label}" Word Problem Fluency score reflects performance in Know & Show ${ks} (round 1 only).  This score is given out of 25.`;
    },
  },
] as const;
sectors satisfies readonly SectorConfig<GoalsIndexData>[];

const detailsByLevel = {
  [GoalsIndexPhase.Early]: {
    stickers: '1000',
    ss: '1, 2, and 3',
    ks: '1 and 2',
  },
  [GoalsIndexPhase.Elementary]: {
    stickers: '2000',
    ss: '2, 3, and 4',
    ks: '3 and 4',
  },
  [GoalsIndexPhase.Intermediate]: {
    stickers: '3500',
    ss: '4, 5, and 6',
    ks: '5 and 6',
  },
  [GoalsIndexPhase.Advanced]: {
    stickers: '5000',
    ss: '6, 7, and 8',
    ks: '7 and 8',
  },
} as const satisfies Record<string, LevelDetails>;

/**
 * Presentational Goals Index chart component using Raphael.js.
 * This component renders the chart given pre-fetched data.
 * For Storybook and testing purposes.
 */
export function GoalsIndexChartView({
  data,
  phase,
  expanded,
  showLegend,
  onChartClick,
}: GoalsIndexChartViewProps): JSX.Element {
  // Use the shared Raphael chart hook
  const { paperRef, sectorIndex: descriptionIndex } = useRaphaelChart({
    dimensions: CHART_DIMENSIONS,
    sectors,
    data: data,
    expanded,
  });

  // Get title and description for hovered sector
  const selectedSector = sectors[descriptionIndex];
  const sectorTitle = selectedSector?.name || '';
  const description =
    selectedSector?.getDescription(phase) || defaultDescription;

  return IndexChartFrame({
    paperRef,
    text: {
      title: 'First in Math Goals Index',
      sectorTitle,
      description,
    },
    showLegend,
    onChartClick,
  });
}
